import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UTILITIES } from "@/constants/utilities";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, LogOut, User, Trash2, MapPin, Sparkles, Upload, CloudDownload } from "lucide-react";
import { SnapshotsDialog } from "@/components/SnapshotsDialog";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [snapsOpen, setSnapsOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", notes: "" });
  const [useGps, setUseGps] = useState(true);
  const [gpsBusy, setGpsBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/jobs");
      setJobs(r.data);
    } catch (e) {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createJob = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      if (useGps && navigator.geolocation) {
        setGpsBusy(true);
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject,
              { enableHighAccuracy: true, timeout: 8000 });
          });
          payload.center_lat = pos.coords.latitude;
          payload.center_lng = pos.coords.longitude;
        } catch (_) {
          toast.warning("Couldn't get GPS — job will centre on Sydney (edit later)");
        } finally { setGpsBusy(false); }
      }
      const r = await api.post("/jobs", payload);
      setOpen(false);
      setForm({ name: "", address: "", notes: "" });
      toast.success("Job created");
      nav(`/jobs/${r.data.id}`);
    } catch (err) {
      toast.error("Could not create job");
    } finally {
      setCreating(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await api.delete(`/jobs/${id}`);
      setJobs(js => js.filter(j => j.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-4 md:px-8 py-4 border-b-2 border-foreground bg-background sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src={BRAESIDE_LOGO_URL} alt="Braeside Plumbing and Drainage" className="h-10 md:h-12 w-auto" />
          <div className="hidden sm:block h-8 w-px bg-foreground/40" />
          <span className="font-heading font-black text-lg md:text-xl uppercase tracking-tight">Field Plan</span>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-menu-trigger" className="btn-ghost-rugged text-sm inline-flex items-center gap-2">
            <User className="w-4 h-4" /> {user?.full_name || user?.email}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-2 border-foreground rounded-none">
            <DropdownMenuItem disabled className="font-mono text-xs uppercase">{user?.trade || "Tradesman"}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }} className="cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Trial / subscription state banner — always show status so tradies know
            where they stand. Turns red once trial's expired. */}
        {(() => {
          const status = user?.subscription_status;
          const trialEnds = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
          const isActive = status === "active" || user?.subscription_tier === "lifetime";
          const isTrialing = status === "trialing" && trialEnds && trialEnds.getTime() > Date.now();
          const daysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
          if (isActive) {
            return (
              <div data-testid="sub-banner-active"
                className="mb-6 border-2 border-foreground bg-[#22C55E] px-4 py-2 font-heading font-black uppercase text-sm tracking-widest">
                Active subscription — Field Plan Annual · thanks for supporting the app
              </div>
            );
          }
          if (isTrialing) {
            return (
              <div data-testid="sub-banner-trial"
                className="mb-6 flex flex-wrap items-center justify-between gap-3 border-2 border-foreground bg-[#FFC72C] px-4 py-2">
                <div className="font-heading font-black uppercase text-sm tracking-widest">
                  Free trial — {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
                </div>
                <Link to="/billing" data-testid="banner-upgrade-btn"
                  className="btn-rugged text-[11px] inline-flex items-center gap-1.5">
                  Upgrade AUD $49/yr →
                </Link>
              </div>
            );
          }
          return (
            <div data-testid="sub-banner-expired"
              className="mb-6 flex flex-wrap items-center justify-between gap-3 border-2 border-foreground bg-destructive text-background px-4 py-2">
              <div className="font-heading font-black uppercase text-sm tracking-widest">
                Trial ended — read-only. Subscribe to keep editing.
              </div>
              <Link to="/billing" data-testid="banner-upgrade-btn"
                className="btn-ghost-rugged bg-background text-foreground text-[11px] inline-flex items-center gap-1.5">
                Subscribe AUD $49/yr →
              </Link>
            </div>
          );
        })()}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Your yard</div>
            <h1 className="font-heading font-black uppercase text-4xl md:text-5xl mt-1">Jobs</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="snapshots-btn"
              onClick={() => setSnapsOpen(true)}
              className="btn-ghost-rugged inline-flex items-center gap-2"
              title="View & restore auto-saved cloud snapshots of your jobs"
            >
              <CloudDownload className="w-4 h-4" strokeWidth={3} /> Cloud backups
            </button>
            <label
              data-testid="import-backup-label"
              className="btn-ghost-rugged inline-flex items-center gap-2 cursor-pointer"
              title="Import a Field Plan backup file (.fpjob.json) as a new job"
            >
              <Upload className="w-4 h-4" strokeWidth={3} /> Import backup
              <input
                data-testid="import-backup-input"
                type="file"
                accept=".json,.fpjob,application/json"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  try {
                    const { importJobBackup } = await import("@/lib/jobBackup");
                    const created = await importJobBackup(file);
                    toast.success(`Imported "${created.name}" from backup`);
                    load();
                  } catch (err) {
                    const msg = err?.response?.data?.detail || err?.message || "Import failed";
                    toast.error(msg);
                  }
                }}
              />
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="new-job-btn" className="btn-rugged inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" strokeWidth={3} /> New Job
                </button>
              </DialogTrigger>
            <DialogContent className="border-2 border-foreground rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase text-2xl">New Job</DialogTitle>
                <DialogDescription>Create a new onsite job site.</DialogDescription>
              </DialogHeader>
              <form onSubmit={createJob} className="space-y-4">
                <div>
                  <label className="font-heading font-bold uppercase text-xs tracking-widest">Job name</label>
                  <input data-testid="new-job-name" required
                    value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="font-heading font-bold uppercase text-xs tracking-widest">Address</label>
                  <input data-testid="new-job-address"
                    value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                    className="mt-1 w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="font-heading font-bold uppercase text-xs tracking-widest">Notes</label>
                  <textarea data-testid="new-job-notes" rows={3}
                    value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    className="mt-1 w-full border-2 border-foreground bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <label className="flex items-center gap-3 border-2 border-foreground bg-background px-3 py-2 cursor-pointer">
                  <input data-testid="new-job-use-gps" type="checkbox"
                    checked={useGps} onChange={(e) => setUseGps(e.target.checked)}
                    className="w-4 h-4 accent-foreground" />
                  <div className="flex-1">
                    <div className="font-heading font-bold uppercase text-xs tracking-widest">Use my GPS for site centre</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {gpsBusy ? "Getting fix…" : "Best onsite; falls back to Sydney if denied"}
                    </div>
                  </div>
                </label>
                <DialogFooter>
                  <button type="submit" data-testid="new-job-submit" disabled={creating} className="btn-rugged w-full">
                    {creating ? "Creating…" : "Create & Open"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Utility legend */}
        <div className="mb-8 border-2 border-foreground bg-background p-4">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Utility legend</div>
          <div className="flex flex-wrap gap-2">
            {UTILITIES.map(u => (
              <span key={u.key}
                className="flex items-center gap-2 border-2 border-foreground px-2.5 py-1 font-heading font-bold uppercase text-[11px] tracking-wider">
                <span className="w-3 h-3 border border-foreground" style={{ background: u.color }} />
                {u.label}
              </span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-sm">Loading jobs…</div>
        ) : jobs.length === 0 ? (
          <div className="border-2 border-dashed border-foreground bp-hatch p-14 text-center">
            <MapPin className="w-10 h-10 mx-auto mb-3" strokeWidth={2.5} />
            <div className="font-heading font-black uppercase text-2xl">No jobs yet</div>
            <p className="mt-2 text-sm text-muted-foreground">Create your first job to start locating services.</p>
            <button data-testid="empty-new-job" onClick={() => setOpen(true)} className="btn-rugged mt-6 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" strokeWidth={3} /> New Job
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {jobs.map(j => (
              <div key={j.id} data-testid={`job-card-${j.id}`}
                className="border-2 border-foreground bg-background p-5 shadow-block-sm flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Site</div>
                    <div className="font-heading font-black uppercase text-2xl mt-0.5 truncate">{j.name}</div>
                    {j.address && <div className="mt-1 text-xs text-muted-foreground truncate">{j.address}</div>}
                  </div>
                  <button data-testid={`job-delete-${j.id}`} onClick={() => del(j.id)} className="p-2 border-2 border-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="border-2 border-foreground px-2 py-0.5 font-mono text-[10px] uppercase">{(j.pins||[]).length} pins</span>
                  <span className="border-2 border-foreground px-2 py-0.5 font-mono text-[10px] uppercase">{(j.lines||[]).length} runs</span>
                  <span className="border-2 border-foreground px-2 py-0.5 font-mono text-[10px] uppercase">{(j.strokes||[]).length} sketches</span>
                </div>
                <button data-testid={`job-open-${j.id}`} onClick={() => nav(`/jobs/${j.id}`)}
                  className="btn-rugged mt-5 text-sm">Open</button>
              </div>
            ))}
          </div>
        )}
      </main>
      <SnapshotsDialog
        open={snapsOpen}
        onOpenChange={setSnapsOpen}
        onRestored={() => load()}
      />
    </div>
  );
};

export default Dashboard;
